import { todayInTimezone } from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert, Badge, Card } from '../../components/ui/display';
import { Field, Input } from '../../components/ui/inputs';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useToast } from '../../components/ui/Toast';
import { useDebounce } from '../../hooks/useDebounce';
import { formatDate, formatMoney } from '../../utils/format';
import { sources } from '../masters/sources';

export interface BillingChange {
  id: string;
  effectiveFrom: string;
  fromType: 'PREPAID' | 'POSTPAID';
  toType: 'PREPAID' | 'POSTPAID';
  managerAccount: { id: string; name: string } | null;
  previousManagerAccount: { id: string; name: string } | null;
  prepaidBalance: string;
  entriesMoved: number;
  balanceSettledOn: string | null;
  balanceSettlementNote: string | null;
  notes: string | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  canUndo: boolean;
}

interface ConversionPreview {
  effectiveFrom: string;
  managerAccount: { id: string; name: string };
  gstAccountChanges: boolean;
  entriesMoved: number;
  prepaidBalance: string;
}

const errorText = (err: unknown) =>
  err instanceof ApiError
    ? [err.message, ...err.errors.map((e) => e.message)]
        .filter((m, i, all) => m && all.indexOf(m) === i)
        .join(' ')
    : 'Something went wrong';

export const billingChangesKey = (accountId: string) => ['billing', 'changes', accountId];

export function useBillingChanges(accountId: string) {
  return useQuery({
    queryKey: billingChangesKey(accountId),
    queryFn: () => api.get<BillingChange[]>(`/ads-accounts/${accountId}/billing-changes`),
  });
}

function useInvalidateAccount() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['billing'] });
    void queryClient.invalidateQueries({ queryKey: ['resource'] });
    void queryClient.invalidateQueries({ queryKey: ['marketing'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

/** Prepaid → an MCC's credit line from a date, with a preview of what moves. */
export function ConvertToCreditLineDialog({
  open,
  onClose,
  account,
}: {
  open: boolean;
  onClose: () => void;
  account: { id: string; accountName: string; advertisingPlatformId: string };
}) {
  const toast = useToast();
  const invalidate = useInvalidateAccount();
  const [mccId, setMccId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayInTimezone());
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) {
      setMccId('');
      setEffectiveFrom(todayInTimezone());
      setNotes('');
    }
  }, [open]);

  const body = { managerAccountId: mccId, effectiveFrom, notes };
  const debouncedDate = useDebounce(effectiveFrom, 300);
  const preview = useQuery({
    queryKey: ['billing', 'conversion-preview', account.id, mccId, debouncedDate],
    queryFn: () =>
      api.post<ConversionPreview>(`/ads-accounts/${account.id}/convert-to-credit-line/preview`, {
        managerAccountId: mccId,
        effectiveFrom: debouncedDate,
      }),
    enabled: open && !!mccId && !!debouncedDate,
    retry: false,
  });
  const convert = useMutation({
    mutationFn: () => api.post(`/ads-accounts/${account.id}/convert-to-credit-line`, body),
    onSuccess: () => {
      toast.success('Account moved to the credit line');
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(errorText(err)),
  });
  const p = preview.data;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Convert to credit line"
      description={`${account.accountName}: spend from the chosen date is billed on the manager account's credit line. Earlier days stay prepaid.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => convert.mutate()}
            loading={convert.isPending}
            disabled={!p || preview.isFetching}
          >
            Convert
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Manager account (MCC)" htmlFor="convert-mcc" required>
          <SearchableSelect
            inputId="convert-mcc"
            value={mccId}
            onChange={(v) => setMccId(v ?? '')}
            remote={sources.managerAccount({
              status: 'ACTIVE',
              advertisingPlatformId: account.advertisingPlatformId,
            })}
          />
        </Field>
        <Field
          label="Billed on the credit line from"
          htmlFor="convert-date"
          required
          help="The first day the platform bills through the MCC"
        >
          <Input
            id="convert-date"
            type="date"
            value={effectiveFrom}
            max={todayInTimezone()}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </Field>
        <Field label="Notes" htmlFor="convert-notes">
          <Input id="convert-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {preview.isError && <Alert tone="error">{errorText(preview.error)}</Alert>}
        {p && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <ul className="space-y-1.5 text-slate-700">
              <li>
                <strong>{p.entriesMoved}</strong> spend {p.entriesMoved === 1 ? 'entry' : 'entries'}{' '}
                from {formatDate(p.effectiveFrom)} will count on the credit line of{' '}
                <strong>{p.managerAccount.name}</strong>.
              </li>
              <li>
                Prepaid balance left at the end of {formatDate(addDay(p.effectiveFrom, -1))}:{' '}
                <strong className="num">{formatMoney(p.prepaidBalance)}</strong>. It stays shown on
                this account until you mark it settled (refund, used up or written off).
              </li>
              {p.gstAccountChanges && (
                <li className="text-amber-700">
                  The MCC is on a different GST account; spend from this date moves to it.
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}

const addDay = (ymd: string, n: number) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Billing history, with the leftover prepaid balance and undo of the latest change. */
export function BillingHistoryCard({
  accountId,
  canWrite,
}: {
  accountId: string;
  canWrite: boolean;
}) {
  const toast = useToast();
  const invalidate = useInvalidateAccount();
  const changes = useBillingChanges(accountId);
  const [undoing, setUndoing] = useState<BillingChange | null>(null);
  const [settling, setSettling] = useState<BillingChange | null>(null);
  const [settledOn, setSettledOn] = useState(todayInTimezone());
  const [note, setNote] = useState('');

  const undo = useMutation({
    mutationFn: (c: BillingChange) =>
      api.delete(`/ads-accounts/${accountId}/billing-changes/${c.id}`),
    onSuccess: () => {
      toast.success('Conversion undone; the account is prepaid again');
      setUndoing(null);
      invalidate();
    },
    onError: (err) => toast.error(errorText(err)),
  });
  const settle = useMutation({
    mutationFn: (v: { change: BillingChange; settledOn: string; note: string }) =>
      api.put(`/ads-accounts/${accountId}/billing-changes/${v.change.id}/prepaid-balance`, {
        settledOn: v.settledOn,
        note: v.note,
      }),
    onSuccess: () => {
      toast.success('Prepaid balance updated');
      setSettling(null);
      invalidate();
    },
    onError: (err) => toast.error(errorText(err)),
  });

  if (!changes.data?.length) return null;
  return (
    <Card title="Billing history" bodyClassName="p-0">
      <ul className="divide-y divide-slate-100">
        {changes.data.map((c) => (
          <li key={c.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
            <div className="space-y-1 text-sm">
              <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
                From {formatDate(c.effectiveFrom)}:
                <Badge tone="blue">{c.fromType === 'PREPAID' ? 'Prepaid' : 'Credit line'}</Badge>
                <ArrowRight className="size-3.5 text-slate-400" />
                <Badge tone="violet">{c.toType === 'PREPAID' ? 'Prepaid' : 'Credit line'}</Badge>
                {c.managerAccount && (
                  <Link
                    to={`/mcc-accounts/${c.managerAccount.id}`}
                    className="text-brand-700 hover:underline"
                  >
                    {c.managerAccount.name}
                  </Link>
                )}
              </p>
              <p className="text-slate-600">
                {c.entriesMoved} already-entered {c.entriesMoved === 1 ? 'day' : 'days'} moved over
                · by {c.createdBy?.name ?? '—'} on {formatDate(c.createdAt.slice(0, 10))}
                {c.notes ? ` · ${c.notes}` : ''}
              </p>
              {c.fromType === 'PREPAID' && (
                <p className="text-slate-600">
                  Prepaid balance left:{' '}
                  <strong className="num text-slate-900">{formatMoney(c.prepaidBalance)}</strong>{' '}
                  {c.balanceSettledOn ? (
                    <Badge tone="green">
                      settled {formatDate(c.balanceSettledOn)}
                      {c.balanceSettlementNote ? ` · ${c.balanceSettlementNote}` : ''}
                    </Badge>
                  ) : (
                    Number(c.prepaidBalance) !== 0 && <Badge tone="amber">open</Badge>
                  )}
                </p>
              )}
            </div>
            {canWrite && (
              <div className="flex flex-wrap gap-2">
                {c.fromType === 'PREPAID' &&
                  (c.balanceSettledOn ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={settle.isPending}
                      onClick={() => settle.mutate({ change: c, settledOn: '', note: '' })}
                    >
                      Mark open again
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setSettledOn(todayInTimezone());
                        setNote('');
                        setSettling(c);
                      }}
                    >
                      Mark balance settled
                    </Button>
                  ))}
                {c.canUndo && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Undo2 className="size-4" />}
                    onClick={() => setUndoing(c)}
                  >
                    Undo
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={!!undoing}
        title="Undo this billing change?"
        message={
          undoing && (
            <>
              The account goes back to prepaid, and its spend from{' '}
              {formatDate(undoing.effectiveFrom)} moves off the credit line again.
            </>
          )
        }
        confirmLabel="Undo"
        loading={undo.isPending}
        onConfirm={() => undoing && undo.mutate(undoing)}
        onCancel={() => setUndoing(null)}
      />
      <Modal
        open={!!settling}
        onClose={() => setSettling(null)}
        title="Prepaid balance settled"
        description="Record what happened to the balance left on the prepaid account (refund received, used up, written off)."
        footer={
          <>
            <Button variant="secondary" onClick={() => setSettling(null)}>
              Cancel
            </Button>
            <Button
              loading={settle.isPending}
              disabled={!settledOn}
              onClick={() => settling && settle.mutate({ change: settling, settledOn, note })}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Settled on" htmlFor="settled-on" required>
            <Input
              id="settled-on"
              type="date"
              value={settledOn}
              max={todayInTimezone()}
              onChange={(e) => setSettledOn(e.target.value)}
            />
          </Field>
          <Field label="Note" htmlFor="settled-note" help="e.g. Refund received, UTR …">
            <Input id="settled-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </Card>
  );
}
